import { injectable } from "inversify";
import { IOrganisationCard, PortalType } from "@studyportals/domain-client";

import { Organisation } from "@/models/Organisation";
import { Deployment } from "@/platform/Deployment";
import { EventAggregationService } from '@/platform/EventAggregationService';
import { DomainApiProjectionOrganisationForSearch } from "@/infrastructure/DomainApiProjectionOrganisationForSearch";
import { OrganisationSearchResult } from "./OrganisationSearchResult";

@injectable()
export abstract class OrganisationPresenter<T> {

	private readonly portalType: PortalType;

	public constructor(
		protected readonly eventAggregationService: EventAggregationService,
		protected readonly domainApiProjection: DomainApiProjectionOrganisationForSearch,
		window: Window
	) {
		this.portalType = window.PortalType;
	}

	public abstract search(filters: T, resultSize: number, resultsOffset: number): Promise<OrganisationSearchResult>;

	protected reconstituteAll(organisations: readonly IOrganisationCard[]): readonly Organisation[] {
		return organisations.map((s) => this.reconstituteOrganisation(s));
	}

	private reconstituteOrganisation(organisation: IOrganisationCard): Organisation {

		const rankPositionsPerDiscipline = Deployment.isLatestDomainApiResponse(organisation)
			? organisation.getOrganisationMetaRankingPositionsByDiscipline()
			: null;


		return new Organisation(
			organisation.getOrganisationId().toString(),
			organisation.getOrganisationName(),
			organisation.getOrganisationRelativeLinkOfDetailPage(),
			organisation.getOrganisationHasDeliveryMethodOnline(),
			organisation.getOrganisationHasDeliveryMethodOnCampus(),
			organisation.getOrganisationHasDeliveryMethodBlended(),
			organisation.getOrganisationLocationCityName(),
			organisation.getOrganisationLocationCityRelativeLinkOfDetailPage(),
			organisation.getOrganisationLocationCountryIsoCode(),
			organisation.getOrganisationLocationCountryRelativeLinkOfDetailPage(),
			organisation.getOrganisationMayShowLogoSquare(),
			organisation.getOrganisationLogoSquareCdn(),
			organisation.getOrganisationSummary(),
			organisation.getOrganisationIsPremium(),
			organisation.getOrganisationVenuesCount(),
			this.getNumberOfProgrammes(organisation),
			Deployment.isLatestDomainApiResponse(organisation) ? this.getScholarshipsCount(organisation) : null,
			organisation.isForProfitFunding(),
			organisation.isNonProfitFunding(),
			organisation.isPublicFunding(),
			organisation.getOrganisationGlobalMetaRankingPosition(),
			organisation.getOrganisationGlobalPositionPercentage(),
			rankPositionsPerDiscipline,
		);
	}

	private getNumberOfProgrammes(organisation: IOrganisationCard): number | null {
		switch (this.portalType) {
			case PortalType.MASTER:
				return organisation.getNumberOfProgrammesForMasters();
			case PortalType.BACHELOR:
				return organisation.getNumberOfProgrammesForBachelors();
			case PortalType.PHD:
				return organisation.getNumberOfProgrammesForPhd();
			case PortalType.SHORT:
				return organisation.getNumberOfProgrammesForShortCourses();
			case PortalType.DISTANCE_LEARNING:
				return organisation.getNumberOfProgrammesForDistanceLearning();
			default:
				return null;
		}
	}

	private getScholarshipsCount(organisation: IOrganisationCard): number | null {
		switch (this.portalType) {
			case PortalType.MASTER:
				return organisation.getScholarshipsCountForMasters();
			case PortalType.BACHELOR:
				return organisation.getScholarshipsCountForBachelors();
			case PortalType.PHD:
				return organisation.getScholarshipsCountForPhd();
			default:
				return null;
		}
	}
}
